import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";

import {
  formatUsd,
  listAllDisbursements,
  markDisbursementForfeited,
  markDisbursementPaid,
  placeLabel,
  type AdminDisbursementRow,
  type PrizeDisbursementStatus,
} from "../../../lib/winners";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminModal,
  AdminPageHeader,
  AdminEmpty,
  FilterChip,
} from "../../../components/admin/ui";
import { StatusPill, type StatusPillTone } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;

type StatusFilter = "all" | PrizeDisbursementStatus;
type PaymentRail = "manual" | "qbo" | "venmo" | "zelle" | "ach" | "check";

const STATUS_LABELS: Record<PrizeDisbursementStatus, string> = {
  pending_tax_info: "Pending W-9",
  ready_to_pay: "Ready to pay",
  paid: "Paid",
  forfeited: "Forfeited",
};

const STATUS_TONE: Record<PrizeDisbursementStatus, StatusPillTone> = {
  pending_tax_info: "amber",
  ready_to_pay: "amber",
  paid: "sage",
  forfeited: "grey",
};

const PAYMENT_RAILS: PaymentRail[] = ["manual", "venmo", "zelle", "ach", "check", "qbo"];

export default function AdminDisbursementsScreen() {
  const [rows, setRows] = useState<AdminDisbursementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<AdminDisbursementRow | null>(null);
  const [actionKind, setActionKind] = useState<"pay" | "forfeit" | null>(null);

  const filterUrl = useAdminListFilter({
    keys: ["q", "status", "competition"],
    defaults: { q: "", status: "all", competition: "all" },
  });
  const search = filterUrl.get("q");
  const statusFilter = filterUrl.get("status") as StatusFilter;
  const competitionFilter = filterUrl.get("competition");
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filterUrl.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listAllDisbursements());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load disbursements");
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  const competitions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) m.set(r.competition_id, r.competition_name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (competitionFilter !== "all" && r.competition_id !== competitionFilter) return false;
      if (q) {
        const hay = (r.student_name ?? "") + " " + (r.student_email ?? "") + " " + r.competition_name;
        if (!hay.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, competitionFilter]);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.lg, maxWidth: 1180 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Disbursements"
          title="Prize payouts"
          subtitle="Mark each winning student's payout paid as you send it. The rail is informational for v1 — QBO Contractor Payments swap is queued."
        />

        <View style={{ gap: 8, marginBottom: SPACING.md }}>
          <AdminInput
            theme={THEME}
            value={searchInput}
            onChangeText={setSearchInput}
            placeholder="Search by student or competition…"
          />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            {(["all", "pending_tax_info", "ready_to_pay", "paid", "forfeited"] as StatusFilter[]).map((s) => (
              <FilterChip
                key={s}
                theme={THEME}
                label={s === "all" ? "All" : STATUS_LABELS[s as PrizeDisbursementStatus]}
                active={statusFilter === s}
                onPress={() => filterUrl.set({ status: s })}
              />
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            <FilterChip
              theme={THEME}
              label="All competitions"
              active={competitionFilter === "all"}
              onPress={() => filterUrl.set({ competition: "all" })}
            />
            {competitions.map(([cid, name]) => (
              <FilterChip
                key={cid}
                theme={THEME}
                label={name}
                active={competitionFilter === cid}
                onPress={() => filterUrl.set({ competition: cid })}
              />
            ))}
          </View>
        </View>

        {loading ? (
          <AdminLoading theme={THEME} label="Loading disbursements…" />
        ) : err ? (
          <AdminError theme={THEME} message={err} onRetry={() => void reload()} />
        ) : filtered.length === 0 ? (
          <AdminEmpty
            theme={THEME}
            title="No disbursements match"
            body="Disbursements appear here as competitions close and admin approves the auto-pick."
          />
        ) : (
          <View style={{ gap: 8 }}>
            {filtered.map((r) => (
              <DisbursementRow
                key={r.id}
                row={r}
                onMarkPaid={() => {
                  setActionTarget(r);
                  setActionKind("pay");
                }}
                onMarkForfeited={() => {
                  setActionTarget(r);
                  setActionKind("forfeit");
                }}
              />
            ))}
          </View>
        )}
      </View>

      <PayModal
        row={actionKind === "pay" ? actionTarget : null}
        onClose={() => {
          setActionTarget(null);
          setActionKind(null);
        }}
        onSaved={() => {
          setActionTarget(null);
          setActionKind(null);
          void reload();
        }}
      />
      <ForfeitModal
        row={actionKind === "forfeit" ? actionTarget : null}
        onClose={() => {
          setActionTarget(null);
          setActionKind(null);
        }}
        onSaved={() => {
          setActionTarget(null);
          setActionKind(null);
          void reload();
        }}
      />
    </ScrollView>
  );
}

function DisbursementRow({
  row,
  onMarkPaid,
  onMarkForfeited,
}: {
  row: AdminDisbursementRow;
  onMarkPaid: () => void;
  onMarkForfeited: () => void;
}) {
  const isFinal = row.status === "paid" || row.status === "forfeited";
  return (
    <View
      style={{
        padding: 14,
        borderRadius: RADII.md,
        backgroundColor: THEME.surfaceRaisedBg,
        borderWidth: STROKES.hairline,
        borderColor: THEME.border,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 13.5, color: THEME.textOnSurface }} numberOfLines={1}>
            {row.student_name ?? row.student_email ?? "(no profile)"}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
            {row.competition_name} · {placeLabel(row.place)}
          </Text>
        </View>
        <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 22, color: THEME.accent }}>
          {formatUsd(row.amount_cents)}
        </Text>
        <StatusPill theme={THEME} label={STATUS_LABELS[row.status]} tone={STATUS_TONE[row.status]} />
      </View>
      <View style={{ marginTop: 8, flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
          {row.tax_info_required
            ? "W-9 " + (row.tax_info_status === "received" ? "received" : row.tax_info_status === "requested" ? "requested" : "not started")
            : "1099 not required (< $600)"}
          {row.payment_rail && row.payment_rail !== "manual" ? " · rail " + row.payment_rail : ""}
          {row.payment_reference ? " · " + row.payment_reference : ""}
        </Text>
        <View style={{ flex: 1 }} />
        {!isFinal ? (
          <>
            <AdminButton theme={THEME} label="Mark paid" size="sm" onPress={onMarkPaid} />
            <AdminButton theme={THEME} label="Forfeit" size="sm" variant="destructive" onPress={onMarkForfeited} />
          </>
        ) : (
          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
            {row.status === "paid" && row.paid_at
              ? "Paid " + new Date(row.paid_at).toLocaleDateString()
              : row.status === "forfeited" && row.forfeited_at
                ? "Forfeited " + new Date(row.forfeited_at).toLocaleDateString()
                : ""}
          </Text>
        )}
      </View>
    </View>
  );
}

function PayModal({
  row,
  onClose,
  onSaved,
}: {
  row: AdminDisbursementRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rail, setRail] = useState<PaymentRail>("manual");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset transient state whenever a fresh row opens.
  useEffect(() => {
    if (row) {
      setRail("manual");
      setReference("");
      setErr(null);
      setBusy(false);
    }
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!row) return;
    setBusy(true);
    setErr(null);
    const { error } = await markDisbursementPaid(row.id, rail, reference.trim() || null);
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved();
  }

  return (
    <AdminModal
      theme={THEME}
      visible={!!row}
      onClose={onClose}
      eyebrow="Disbursement"
      title={row ? `Mark paid: ${formatUsd(row.amount_cents)} to ${row.student_name ?? row.student_email}` : "Mark paid"}
      footer={
        <>
          <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={onClose} />
          <AdminButton theme={THEME} label="Mark paid" loading={busy} onPress={() => void submit()} />
        </>
      }
    >
      <View style={{ gap: 14 }}>
        <View style={{ gap: 6 }}>
          <Text style={{ fontFamily: THEME.bodySemiBoldFont, fontSize: 10, letterSpacing: 2.2, textTransform: "uppercase", color: THEME.textOnSurfaceFaint }}>
            Payment rail
          </Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            {PAYMENT_RAILS.map((r) => (
              <FilterChip key={r} theme={THEME} label={r} active={rail === r} onPress={() => setRail(r)} />
            ))}
          </View>
        </View>
        <AdminInput
          theme={THEME}
          label="Reference (optional)"
          value={reference}
          onChangeText={setReference}
          placeholder="Venmo: @handle / 2026-05-08"
          autoCapitalize="none"
        />
        {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}
      </View>
    </AdminModal>
  );
}

function ForfeitModal({
  row,
  onClose,
  onSaved,
}: {
  row: AdminDisbursementRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (row) {
      setReason("");
      setErr(null);
      setBusy(false);
    }
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!row) return;
    if (!reason.trim()) {
      setErr("Reason is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await markDisbursementForfeited(row.id, reason.trim());
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved();
  }

  return (
    <AdminModal
      theme={THEME}
      visible={!!row}
      onClose={onClose}
      eyebrow="Disbursement"
      title={row ? `Forfeit: ${formatUsd(row.amount_cents)} to ${row.student_name ?? row.student_email}` : "Forfeit"}
      subtitle="Forfeited prizes do NOT auto-roll to the next-place team — that's a manual override decision."
      footer={
        <>
          <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={onClose} />
          <AdminButton theme={THEME} label="Forfeit" variant="destructive" loading={busy} onPress={() => void submit()} />
        </>
      }
    >
      <View style={{ gap: 14 }}>
        <AdminInput
          theme={THEME}
          label="Reason *"
          value={reason}
          onChangeText={setReason}
          multiline
          minHeight={80}
          placeholder="Student didn't respond to repeated tax-info requests over 60 days."
        />
        {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}
      </View>
    </AdminModal>
  );
}
